const loadScript = require('./load-script');

/**
 * jsDelivr CDN URL serving Crowdin distribution files from the crowdin-dist
 * git branch.  jsDelivr unconditionally sets Access-Control-Allow-Origin: *,
 * so cross-origin fetch() calls succeed without any browser plugin.
 * The branch is refreshed daily by the "Sync Crowdin Distribution" workflow.
 * Structure mirrors https://distributions.crowdin.net/<hash>/… exactly.
 * @type {string}
 */
const CROWDIN_DIST_MIRROR = 'https://cdn.jsdelivr.net/gh/LizardByte/i18n@dist';
const CROWDIN_PLATFORM_STYLING_MAX_ATTEMPTS = 100;
const CROWDIN_PLATFORM_STYLING_RETRY_DELAY_MS = 50;
const CROWDIN_INLINE_ELEMENT_SELECTOR = [
    'a',
    'abbr',
    'b',
    'cite',
    'code',
    'del',
    'em',
    'i',
    'ins',
    'kbd',
    'mark',
    'q',
    's',
    'samp',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'time',
    'u',
    'var',
].join(',');

/**
 * Returns a sibling when it is an inline element whose boundary Crowdin may rewrite.
 * @param {Node|null} sibling Candidate sibling.
 * @returns {Element|null} Matching inline element.
 */
function _getCrowdinInlineSibling(sibling) {
    if (!(sibling instanceof globalThis.Element)) return null;
    return sibling.matches(CROWDIN_INLINE_ELEMENT_SELECTOR) ? sibling : null;
}

/**
 * Returns the exact whitespace at the start of a string.
 * @param {string} text Text to inspect.
 * @returns {string} Leading whitespace.
 */
function _getLeadingWhitespace(text) {
    const trimmedText = text.trimStart();
    return text.slice(0, text.length - trimmedText.length);
}

/**
 * Returns the exact whitespace at the end of a string.
 * @param {string} text Text to inspect.
 * @returns {string} Trailing whitespace.
 */
function _getTrailingWhitespace(text) {
    const trimmedText = text.trimEnd();
    return text.slice(trimmedText.length);
}

/**
 * Records whitespace that separates text from inline elements before Crowdin translates the page.
 * @returns {Array<{
 *     node: Text,
 *     leading: boolean,
 *     leadingWhitespace: string,
 *     trailing: boolean,
 *     trailingWhitespace: string,
 *     previousInline: Element|null,
 *     nextInline: Element|null,
 *     whitespaceOnly: boolean,
 *     lastRestoredNode: Text|null,
 *     lastRestoredFrom: string|null
 * }>} Recorded text-node boundaries.
 */
function _captureCrowdinWhitespaceBoundaries() {
    const boundaries = [];
    const walker = document.createTreeWalker(document.body, globalThis.NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();

    while (node !== null) {
        const previousInline = _getCrowdinInlineSibling(node.previousSibling);
        const nextInline = _getCrowdinInlineSibling(node.nextSibling);
        const leadingWhitespace = previousInline === null ? '' : _getLeadingWhitespace(node.data);
        const trailingWhitespace = nextInline === null ? '' : _getTrailingWhitespace(node.data);
        const leading = leadingWhitespace !== '';
        const trailing = trailingWhitespace !== '';

        if (leading || trailing) {
            boundaries.push({
                node,
                leading,
                leadingWhitespace,
                trailing,
                trailingWhitespace,
                previousInline,
                nextInline,
                whitespaceOnly: node.data.trim() === '',
                lastRestoredNode: null,
                lastRestoredFrom: null,
            });
        }

        node = walker.nextNode();
    }

    return boundaries;
}

/**
 * Finds the current text node for a boundary after Crowdin changes the DOM.
 * @param {Object} boundary Recorded whitespace boundary.
 * @returns {Text|null} Current or recreated text node.
 */
function _resolveCrowdinWhitespaceNode(boundary) {
    if (boundary.node.isConnected) return boundary.node;

    const previousReplacement = boundary.previousInline?.nextSibling;
    if (previousReplacement instanceof globalThis.Text && previousReplacement.isConnected) {
        boundary.node = previousReplacement;
        return boundary.node;
    }
    const nextReplacement = boundary.nextInline?.previousSibling;
    if (nextReplacement instanceof globalThis.Text && nextReplacement.isConnected) {
        boundary.node = nextReplacement;
        return boundary.node;
    }
    if (!boundary.whitespaceOnly) return null;

    const replacement = document.createTextNode('');
    if (boundary.previousInline?.isConnected) {
        boundary.previousInline.after(replacement);
    } else if (boundary.nextInline?.isConnected) {
        boundary.nextInline.before(replacement);
    } else {
        return null;
    }

    boundary.node = replacement;
    return boundary.node;
}

/**
 * Restores whitespace that Crowdin removed from translated text-node boundaries.
 * @param {Array<Object>} boundaries Recorded text-node boundaries.
 */
function _restoreCrowdinWhitespaceBoundaries(boundaries) {
    boundaries.forEach((boundary) => {
        const node = _resolveCrowdinWhitespaceNode(boundary);
        if (node === null) return;
        if (boundary.lastRestoredNode === node && boundary.lastRestoredFrom === node.data) return;

        const originalText = node.data;

        const currentLeadingWhitespace = _getLeadingWhitespace(node.data);
        if (boundary.leading && currentLeadingWhitespace !== boundary.leadingWhitespace) {
            node.data = boundary.leadingWhitespace + node.data.slice(currentLeadingWhitespace.length);
        }
        const currentTrailingWhitespace = _getTrailingWhitespace(node.data);
        if (boundary.trailing && currentTrailingWhitespace !== boundary.trailingWhitespace) {
            const translatedTextEnd = node.data.length - currentTrailingWhitespace.length;
            node.data = node.data.slice(0, translatedTextEnd) + boundary.trailingWhitespace;
        }
        if (node.data !== originalText) {
            // A translator may observe this correction and remove the same
            // whitespace again. Do not repeat the correction indefinitely.
            boundary.lastRestoredNode = node;
            boundary.lastRestoredFrom = originalText;
        }
    });
}

/**
 * Creates the Website Translator callback and observes later translation mutations.
 * @param {Array<Object>} boundaries Recorded text-node boundaries.
 * @returns {Function} Website Translator callback.
 */
function _createCrowdinTranslationCallback(boundaries) {
    const translationObserver = new globalThis.MutationObserver(restoreAndObserve);

    function restoreAndObserve() {
        translationObserver.disconnect();
        _restoreCrowdinWhitespaceBoundaries(boundaries);
        translationObserver.observe(document.body, {
            characterData: true,
            childList: true,
            subtree: true,
        });
    }

    return restoreAndObserve;
}

/**
 * Monkey-patches globalThis.fetch to redirect Crowdin distribution requests to
 * the self-hosted GitHub Pages mirror.
 *
 * Must be called BEFORE proxy-translator.js is loaded so that every fetch()
 * the script makes is already intercepted.
 *
 * Idempotent – installs the interceptor at most once per page.
 */
function _installCrowdinFetchInterceptor() {
    if (typeof globalThis.fetch !== 'function') return;
    if (globalThis._crowdinMirrorInstalled) return;
    globalThis._crowdinMirrorInstalled = true;

    const _origFetch = globalThis.fetch.bind(globalThis);

    globalThis.fetch = function crowdinMirrorFetch(url, options) {
        if (typeof url === 'string') {
            let parsed;
            try {
                parsed = new URL(url);
            } catch {
                // Not a valid absolute URL – pass through unchanged.
            }
            // Use exact hostname comparison to avoid prefix-match bypasses
            // (e.g. distributions.crowdin.net.evil.com) that would be flagged
            // by incomplete URL sanitisation checks.
            if (parsed?.protocol === 'https:' && parsed.hostname === 'distributions.crowdin.net') {
                const mirroredUrl = CROWDIN_DIST_MIRROR + parsed.pathname + parsed.search + parsed.hash;
                return _origFetch(mirroredUrl, options);
            }
        }
        return _origFetch(url, options);
    };
}

/**
 * Re-attempts platform styling while Crowdin inserts the language picker.
 * @param {string} platform - UI platform ('dockle' or 'jekyll').
 * @param {number} attempt - Current retry count.
 */
function _retryCrowdinPlatformStyling(platform, attempt) {
    if (attempt >= CROWDIN_PLATFORM_STYLING_MAX_ATTEMPTS) {
        return;
    }

    globalThis.setTimeout(function() {
        _applyCrowdinPlatformStyling(platform, attempt + 1);
    }, CROWDIN_PLATFORM_STYLING_RETRY_DELAY_MS);
}

/**
 * Applies platform-specific placement after the Crowdin picker exists.
 * @param {string} platform - UI platform ('dockle' or 'jekyll').
 * @param {number} attempt - Current retry count.
 */
function _applyCrowdinPlatformStyling(platform, attempt = 0) {
    if (platform === 'jekyll') {
        return;
    }

    const container = document.getElementById('crowdin-language-picker');

    if (container === null) {
        _retryCrowdinPlatformStyling(platform, attempt);
        return;
    }

    container.classList.remove('cr-position-bottom-left');
    container.classList.add('dockle-crowdin-picker');
    container.style.position = 'fixed';
    container.style.left = 'auto';
    container.style.bottom = 'auto';
    document.body.appendChild(container);
}

/**
 * Initializes Crowdin translation widget based on project and UI platform.
 * @param {string} project - Project name ('LizardByte' or 'LizardByte-docs').
 * @param {string} platform - UI platform ('dockle' or 'jekyll').
 */
function initCrowdIn(project = 'LizardByte', platform = 'jekyll') {
    // Input validation
    if (!['LizardByte', 'LizardByte-docs'].includes(project)) {
        console.error('Invalid project. Must be "LizardByte" or "LizardByte-docs"');
        return;
    }
    if (!['dockle', 'jekyll'].includes(platform)) {
        console.error('Invalid UI. Must be "dockle" or "jekyll"');
        return;
    }

    // Redirect distribution CDN requests to our self-hosted GitHub Pages mirror
    // before the script is even loaded so every fetch() it makes is intercepted.
    _installCrowdinFetchInterceptor();

    function initializeCrowdin(error) {
        if (error) return;

        // Configure base settings based on project
        const projectSettings = {
            'LizardByte': {
                baseUrl: "https://app.lizardbyte.dev",
                distribution: "458f881791aebba1d4dde491bw4",
            },
            'LizardByte-docs': {
                baseUrl: "https://docs.lizardbyte.dev",
                distribution: "d6c830ba4b41106fefe5d391bw4",
            }
        };

        let languageTitles = {
            "bg": "Български (Bulgarian)",
            "cs": "Čeština (Czech)",
            "de": "Deutsch (German)",
            "en": "English",
            "en-GB": "English, United Kingdom",
            "en-US": "English, United States",
            "es-ES": "Español (Spanish)",
            "fr": "Français (French)",
            "hu": "Magyar (Hungarian)",
            "it": "Italiano (Italian)",
            "ja": "日本語 (Japanese)",
            "ko": "한국어 (Korean)",
            "pl": "Polski (Polish)",
            "pt-BR": "Português, Brasileiro (Portuguese, Brazilian)",
            "pt-PT": "Português (Portuguese)",
            "ru": "Русский (Russian)",
            "sv-SE": "svenska (Swedish)",
            "tr": "Türkçe (Turkish)",
            "uk": "Українська (Ukranian)",
            "vi": "Tiếng Việt (Vietnamese)",
            "zh-CN": "简体中文 (Chinese Simplified)",
            "zh-TW": "繁體中文 (Chinese Traditional)",
        };
        // sort languages by name
        languageTitles = Object.fromEntries(Object.entries(languageTitles).sort((a, b) => a[1].localeCompare(b[1])));

        // use this to allow translations to work on PR preview builds
        let currentBaseUrl = globalThis.location.origin;

        // Initialize Crowdin translator
        const whitespaceBoundaries = _captureCrowdinWhitespaceBoundaries();

        globalThis.proxyTranslator.init({
            baseUrl: currentBaseUrl,
            callback: _createCrowdinTranslationCallback(whitespaceBoundaries),
            distribution: projectSettings[project].distribution,
            defaultLanguage: "en",
            languageTitles: languageTitles,
            showDefaultLanguageInUrl: false,
            languageRoutingMethod: "query",
            position: "bottom-left",
            submenuPosition: "top-left",
            poweredBy: false,
        });

        // Apply styling based on UI framework
        _applyCrowdinPlatformStyling(platform);
    }

    function loadCrowdinScript() {
        loadScript('https://website-translator.app.crowdin.net/assets/proxy-translator.js', initializeCrowdin);
    }

    // A pending third-party script delays the browser's load event. Let the
    // documentation finish loading before requesting Crowdin.
    if (document.readyState === 'complete') {
        loadCrowdinScript();
    } else {
        globalThis.addEventListener('load', function() {
            globalThis.setTimeout(loadCrowdinScript, 0);
        }, { once: true });
    }
}

// Expose to the global scope
if (globalThis.window !== undefined) {
    globalThis.initCrowdIn = initCrowdIn;
}

module.exports = initCrowdIn;
